import { getColumnType } from "@/utils/functions";
import { isEqual } from "lodash";
import { ReactNode, createContext, useContext, useRef } from "react";
import { createStore, useStore } from "zustand";
import { persist } from "zustand/middleware";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

export const FilterStringComparisonMap = new Map([
  ["is", "==*"],
  ["is not", "!=*"],
  ["contains", "@=*"],
  ["does not contain", "!@=*"],
  ["starts with", "_=*"],
  ["ends with", "_-=*"],
  ["is empty", ""],
  ["is not empty", ""],
]);

export const FilterGuidComparisonMap = new Map([
  ["is", "=="],
  ["is not", "!="],
]);

export const FilterBooleanComparisonMap = new Map([
  ["is", "=="],
  ["is not", "!="],
]);

export const FilterNumberComparisonMap = new Map([
  ["equals", "=="],
  ["greater than", ">"],
  ["less than", "<"],
  ["greater than or equal to", ">="],
  ["less than or equal to", "<="],
]);

export const FilterDateComparisonMap = new Map([
  ["equals", "==*"],
  ["before", "<"],
  ["after", ">"],
  ["on or before", "<="],
  ["on or after", ">="],
]);

export type SortBy = "asc" | "desc" | string;

export type ComparisonString =
  | "is"
  | "is not"
  | "contains"
  | "does not contain"
  | "starts with"
  | "ends with"
  | "is empty"
  | "is not empty"
  | string;

export type ComparisonNumber =
  | "equals"
  | "greater than"
  | "less than"
  | "greater than or equal to"
  | "less than or equal to";

export type ComparisonDate = "equals" | "before" | "after" | string;
export type ComparisonGuid = "is" | "is not" | string;
export type ComparisonBoolean = "is" | "is not" | string;

export const FilterStringComparison: Array<{
  value: ComparisonString;
  label: string;
}> = [
  {
    value: "is",
    label: "is",
  },
  {
    value: "is not",
    label: "is not",
  },
  {
    value: "contains",
    label: "contains",
  },
  {
    value: "does not contain",
    label: "does not contain",
  },
  {
    value: "starts with",
    label: "starts with",
  },
  {
    value: "ends with",
    label: "ends with",
  },
];

export const FilterNumberComparison: Array<{
  value: ComparisonNumber;
  label: string;
}> = [
  {
    value: "equals",
    label: "equals",
  },
  {
    value: "greater than",
    label: "greater than",
  },
  {
    value: "less than",
    label: "less than",
  },
  {
    value: "greater than or equal to",
    label: "greater than or equal to",
  },
  {
    value: "less than or equal to",
    label: "less than or equal to",
  },
];

export const FilterGuidComparison: Array<{
  value: ComparisonGuid;
  label: string;
}> = [
  {
    value: "is",
    label: "is",
  },
  {
    value: "is not",
    label: "is not",
  },
];

export const FilterBooleanComparison: Array<{
  value: ComparisonBoolean;
  label: string;
}> = [
  {
    value: "is",
    label: "is",
  },
  {
    value: "is not",
    label: "is not",
  },
];

export const FilterDateComparison: Array<{
  value: ComparisonDate;
  label: string;
}> = [
  {
    value: "equals",
    label: "equals",
  },
  {
    value: "before",
    label: "before",
  },
  {
    value: "after",
    label: "after",
  },
  {
    value: "on or after",
    label: "on or after",
  },
  {
    value: "on or before",
    label: "on or before",
  },
];

export type Sort = {
  index: number;
  order: number;
  key: string;
  title: string;
  sortBy: SortBy;
  kind?: "built-in" | "custom";
};

type FilterValueType = "single" | "multiple";

export type FilterType =
  | "string"
  | "date"
  | "number"
  | "guid"
  | "boolean"
  | {
      type: FilterType;
      additionalData: { label: string; value: string | number | boolean }[];
      valueType?: FilterValueType;
    };

export type Filter = {
  open: boolean;
  id?: number;
  key: string;
  title: string;
  compare: ComparisonString | ComparisonDate;
  type: FilterType;
  value: string | number | null | string[];
  kind: "built-in" | "custom";
  valueType?: FilterValueType;
};

export type Filters = Filter[];
export type Sorts = Sort[];

export type TableState = {
  search: string;
  filters: Filters;
  page: number;
  pageSize: number;
  sorts: Sorts;
};

export type TableStateOptional = Optional<
  TableState,
  "page" | "pageSize" | "filters" | "sorts" | "search"
>;

export type TableStateProps = TableState & {
  storageKey: string;
  queryKey: string;
  filterVisible: boolean;
  showSort: boolean;
  toggleSortVisibility: () => void;
  setSortVisibility: (show: boolean) => void;
  setFilterVisibility: (state: boolean) => void;
  updateFilter: (filter: Partial<Filter>) => void;
  addBuiltInFilter: (filters: Filters) => void;
  resetBuiltInFilter: () => void;
  addFilter: (filter: Filter) => void;
  setSearch: (search: string) => void;
  removeFilter: (id: number) => void;
  addBuiltInSort: (sorts: Sorts) => void;
  addSort: (sort: Sort, type?: "built-in" | "custom") => void;
  removeSort: (index: number) => void;
  updateSort: (index: number, sort: Sort) => void;
  setSorts: (sorts: Sorts) => void;
  nextPage: () => void;
  prevPage: () => void;
  firstPage: () => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  removeAllSort: () => void;
  showColumnVisible: boolean;
  setColumnVisible: (state: boolean) => void;
  columnVisibility: { [key: string]: boolean };
  setColumnVisibility: (colVisibility: { [key: string]: boolean }) => void;
  removeAllFilter: () => void;
  responseData?: DefaultSieveResponse<any> | null;
  setResponseData: (
    response: DefaultSieveResponse<any> | null | undefined
  ) => void;
};

interface CreateTableStoreProps extends TableStateOptional {
  storageKey?: string;
  queryKey?: string;
}

const createTableStore = ({
  page = 0,
  pageSize = 10,
  filters = [],
  sorts = [],
  search = "",
  storageKey = "table-state",
  queryKey = "table-query",
}: CreateTableStoreProps) =>
  createStore<TableStateProps>()(
    persist<TableStateProps>(
      (set, get) => ({
        storageKey: storageKey,
        queryKey: queryKey,
        responseData: undefined,
        setResponseData: (data) =>
          set((state) => ({ ...state, responseData: data })),
        showColumnVisible: false,
        setColumnVisible: (columnVisibleState) =>
          set((state) => ({ ...state, showColumnVisible: columnVisibleState })),
        columnVisibility: {},
        setColumnVisibility: (colVisibility) =>
          set((state) => ({ ...state, columnVisibility: colVisibility })),
        filterVisible: false,
        showSort: false,
        toggleSortVisibility: () =>
          set((state) => ({ ...state, showSort: !state.showSort })),
        setSortVisibility: (show: boolean) =>
          set((state) => ({ ...state, showSort: show })),
        setFilterVisibility: (filterState) =>
          set((state) => ({ ...state, filterVisible: filterState })),
        search,
        filters,
        page,
        pageSize,
        sorts,
        setSearch: (search: string) => set((state) => ({ ...state, search })),
        nextPage: () => set((state) => ({ ...state, page: get().page + 1 })),
        prevPage: () => set((state) => ({ ...state, page: get().page - 1 })),
        firstPage: () => set((state) => ({ ...state, page: 1 })),
        setPage: (page: number) => set((state) => ({ ...state, page })),
        setPageSize: (pageSize: number) =>
          set((state) => ({ ...state, pageSize })),
        removeAllSort: () => set((state) => ({ ...state, sorts: [] })),
        removeAllFilter: () =>
          set((state) => ({
            ...state,
            filters: state.filters.filter((e) => e.kind === "built-in"),
          })),
        setSorts: (sorts) => set((state) => ({ ...state, sorts })),
        addSort: (sort: Sort, type: "built-in" | "custom" = "custom") => {
          let findIndex = get().sorts.findIndex(
            (_sort) => _sort.key === sort.key
          );

          if (findIndex !== -1) {
            return;
          }

          set((state) => ({
            ...state,
            sorts: [
              ...state.sorts,
              {
                ...sort,
                index: get().sorts.length + 1,
                order: get().sorts.length + 1,
                kind: type,
              },
            ],
          }));
        },
        removeSort: (index: number) =>
          set((state) => ({
            ...state,
            sorts: get().sorts.filter((e) => e.index !== index),
          })),
        updateSort: (index: number, sort: Sort) => {
          set((state) => ({
            ...state,
            sorts: state.sorts.map((_sort) => {
              if (_sort.index === index) {
                return sort;
              } else {
                return _sort;
              }
            }),
          }));
        },

        addBuiltInFilter: (filters: Filters) => {
          set((state) => ({
            ...state,
            filters: [
              ...state.filters.filter((e) => e.kind !== "built-in"),
              ...filters.map((e) => ({
                ...e,
                valueType: e?.valueType ?? "single",
              })),
            ],
          }));
        },
        addBuiltInSort: (_sorts: Sorts) => {
          set((state) => ({
            ...state,
            sorts: [
              ...state.sorts.filter((e) => e.kind !== "built-in"),
              ..._sorts,
            ],
          }));
        },
        resetBuiltInFilter: () =>
          set((state) => ({
            ...state,
            filters: [...state.filters.filter((e) => e.kind !== "built-in")],
          })),
        addFilter: (filter: Filter) => {
          // let findIndex = get().filters.findIndex(
          //   (_filter) => _filter.key === filter.key
          // );

          // if (findIndex !== -1) {
          //   return;
          // }
          set((state) => ({
            ...state,
            page: 1,
            filters: [
              ...state.filters,
              {
                ...filter,
                id: get().filters.length + 1,
                valueType: filter?.valueType ?? "single",
              },
            ],
          }));
        },
        updateFilter: (filter: Partial<Filter>) => {
          set((state) => ({
            ...state,
            page: 1,
            filters: state.filters.map((_filter) => {
              if (_filter.id === filter.id) {
                return { ..._filter, ...filter };
              } else {
                return _filter;
              }
            }),
          }));
        },

        removeFilter: (id: number) =>
          set((state) => ({
            ...state,
            filters: get().filters.filter((f) => f.id !== id),
          })),
      }),
      {
        name: storageKey,
      }
    )
  );

export const getTableQuery = (state: TableState): SieveRequestPayload => {
  let globalSearch = state.search ? `filter@=${state.search.replaceAll(",", "\\" + ",")}` : "";
  let filters =
    state.filters.length > 0
      ? state.filters
          .filter((f) => f.value !== "")
          .map((f) => {
            let filterComparisonMap: any = null;

            let typeHolder = getColumnType(f.type);
            switch (typeHolder) {
              case "string":
                filterComparisonMap = FilterStringComparisonMap;
                break;
              case "date":
                filterComparisonMap = FilterDateComparisonMap;
                break;
              case "guid":
                filterComparisonMap = FilterGuidComparisonMap;
                break;
              case "boolean":
                filterComparisonMap = FilterBooleanComparisonMap;
                break;
              case "number":
                filterComparisonMap = FilterNumberComparisonMap;
                break;
              default:
                filterComparisonMap = FilterStringComparisonMap;
                break;
            }

            let valueType = f?.valueType ?? "single";

            if (valueType === "single")
              return `${f.key}${filterComparisonMap.get(f.compare)}${f.value}`;

            // Multiple
            return `${f.key}${filterComparisonMap.get(f.compare)}${
              f.value &&
              typeof f.value === "object" &&
              f.value.length > 0 &&
              f.value.filter((e) => e !== "").join("|")
            }`;
          })
          .join(",")
      : "";
  let sorts = state.sorts
    .map((s) => {
      return `${s.sortBy === "asc" ? "" : "-"}${s.key}`;
    })
    .join(",");

  return {
    filters:
      filters !== ""
        ? `${filters}${globalSearch ? `, ${globalSearch}` : ""}`
        : globalSearch,
    sorts: sorts,
    page: state.page + 1,
    pageSize: state.pageSize,
  };
};

const TableContext = createContext<ReturnType<typeof createTableStore> | null>(
  null
);

interface TableProviderProps extends TableStateOptional {
  storageKey?: string;
  queryKey?: string;
  children: ReactNode;
}

function TableProvider({ children, ...opts }: TableProviderProps) {
  const store = useRef(createTableStore(opts));
  return (
    <TableContext.Provider value={store.current}>
      {children}
    </TableContext.Provider>
  );
}

const useTableGetQuery = () =>
  useStore(
    useContext(TableContext)!,
    (state) => ({
      filters: state.filters,
      sorts: state.sorts,
      search: state.search,
      page: state.page,
      pageSize: state.pageSize,
    }),
    (a, b) => {
      return isEqual(getTableQuery(a), getTableQuery(b));
    }
  );

const useTableGetFilters = () =>
  useStore(
    useContext(TableContext)!,
    (state) => state.filters,
    (a, b) => {
      return isEqual(a, b);
    }
  );
const useTableGetSorts = () =>
  useStore(useContext(TableContext)!, (state) => state.sorts, shallow);

function useTable<U>(selector: (state: TableStateProps) => U) {
  return useStore(useContext(TableContext)!, selector, shallow);
}

export {
  TableProvider,
  useTable,
  useTableGetFilters,
  useTableGetQuery,
  useTableGetSorts,
};

interface ExportProps {
  showExportModal: boolean;
  name: string;
  setName: (name: string) => void;
  printMode: boolean;
  setShowExportModal: (printMode: boolean, state: boolean) => void;
  updateExportModal: (newState: boolean) => void;
  printHeaders: any[];
  updatePrintHeaders: (headers: any[]) => void;
  printColumns: any[];
  updatePrintColumns: (columns: any[]) => void;
  isLoading: boolean;
  reset: () => void;
  showPageOptions: boolean;
  updateIsLoading: (isLoading: boolean) => void;
  updateShowPageOptions: (show: boolean) => void;
}

export const useExport = createWithEqualityFn<ExportProps>()((set) => ({
  name: "",
  setName: (name) => set((state) => ({ ...state, name })),
  showExportModal: false,
  printMode: false,
  printHeaders: [],
  updatePrintHeaders: (headers) =>
    set((state) => ({ ...state, printHeaders: headers })),
  printColumns: [],
  updatePrintColumns: (columns) =>
    set((state) => ({
      ...state,
      printColumns: [...state.printColumns, ...columns],
    })),
  setShowExportModal: (printMode, boolState) =>
    set((state) => ({ ...state, printMode, showExportModal: boolState })),
  updateExportModal: (boolState) =>
    set((state) => ({ ...state, showExportModal: boolState })),
  isLoading: false,
  updateIsLoading: (isLoading) =>
    set((state) => ({ ...state, isLoading: isLoading })),
  reset: () =>
    set((state) => ({
      ...state,
      printColumns: [],
      printHeaders: [],
      name: "",
    })),
  showPageOptions: false,
  updateShowPageOptions: (show) =>
    set((state) => ({ ...state, showPageOptions: show })),
}));
